// Dependencies
import React from 'react';
import _ from 'lodash';
import Api from '../api';
import { Config, defaults } from '../utils';

import useEventBus from '../hooks/useEventBus';
import Minicart from './Minicart';
import useGTM from '../hooks/useGTM';
import { transformCartItemData } from '../utils/helpers';

const initialState: any = {
	loading: true,
	errors: {},
	data: {
		items: {},
		notices: {},
		discountAmount: 0,
		cart: {
			subtotal: 0
		}
	},
	...defaults
}

const App = () => {

	// NOTE: Handles data fetching only (both from internal and external sources)
	const [store, updateStore] = React.useState<any>(initialState);
	const [isAddingToCart, setIsAddingToCart] = React.useState<boolean>(false);
	const gtm = useGTM(store.config.general);
	const eventBus = useEventBus();

	const setState = (key: string, value: any) => {
		if (!(key in initialState)) {
			throw Error(`Key ${key} is not valid state.`);
		}
		updateStore((prevStore: any) => ({ ...prevStore, [key]: value instanceof Function ? value(prevStore[key]) : value, }))
	}

	const loadInternalData = async () => {

		const data = {
			...Config.REACT_APP_DATA
		}

		updateStore((prevState: any) => _.merge(defaults, prevState, data));
	}

	const loadExternalData = async () => {
		setState('loading', true);

		try {
			const response = await Api.getCart();

			if (_.isEmpty(response?.errors)) {
				updateStore((prevState: any) => _.merge(defaults, prevState, response));
				setState('loading', false);
			}
		} catch (e) {
			// Silent error
		}

	}

	const handleForceReloadCart = async () => {

		try {
			const response = await Api.getCart();

			if (_.isEmpty(response?.errors)) {
				setState('data', _.merge({ defaults, loading: false }, response.data));
			}
		} catch (e) {
			// Silent error
		}

	}

	const handleProductAddToCart = async ({ id, type, selected, quantity, showMinicart = true, ...props }: any) => {

		if (isAddingToCart) return;
		setIsAddingToCart(true);

		// TODO: Prevent triggering multiple add to cart
		setState('loading', true);

		// NOTE: Assign reccursion limit
		let response: any;

		response = await Api.addProductToCart(id, {
			quantity,
			type,
			selected,
			...props
		});

		if (_.isEmpty(response.errors)) {

			eventBus.dispatch('Minicart.updatedCart', { action: 'add', productID: id, id: response.data.addedCartItemID, data: response.data, quantity: { diff: quantity, selected: quantity, previous: 0 } });
			setState('data', response.data);

			const { addedCartItemID } = response.data;
			const adddedCartItem = response.data.items[addedCartItemID] ?? null;
			if (adddedCartItem) gtm.addProductToCart(transformCartItemData(adddedCartItem), quantity);

			eventBus.dispatch('addedProductToCart', true)
			eventBus.dispatch('Notices.updated', response?.notices);

			if (showMinicart) {
				eventBus.dispatch('toggleMinicartSidebar', true)
			} else {
				eventBus.dispatch('toggleCartPopup', { addedCartItemID: response.data.addedCartItemID })
			}
		} else {
			eventBus.dispatch('addedProductToCart', false);
			eventBus.dispatch('Notices.updated', response.errors.response.data.notices);
		}

		setState('loading', false);
		setIsAddingToCart(false);
	}

	React.useEffect(() => {
		loadInternalData();
		loadExternalData();

		eventBus.on('forceReloadCart', handleForceReloadCart);
		eventBus.on('addProductToCart', handleProductAddToCart);
		return () => eventBus.remove('addProductToCart');
	}, []);

	// TODO: This will be actually the main App component handling data fetching

	return <Minicart {...store} />
}

export default App;
